/**
 * Ficha de un contrato, con su firma.
 *
 * Un contrato firmado en un portal y que sólo se ve como una fila en un
 * listado no sirve el día que hay desacuerdo. Aquí están las condiciones —que
 * SON el contrato— y el trazo de quien lo firmó, con la cadena de custodia.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useSession } from '../../session/session-context.js';
import { ApiError } from '../../session/api-error.js';
import { DatumGroup, Datum, DetailShell, type Tab } from './detail-shell.js';
import { day, dateTime, money } from '../../table/format.js';
import { PdfButton } from '../pdf-button.js';

interface Firma {
  signerName: string;
  signerPosition: string | null;
  signedAt: string | null;
  signedDocumentHash: string;
  deviceId: string | null;
  capturedBy: string | null;
  strokeSvg: string | null;
  unavailableReason: string | null;
}

interface Detalle {
  contract: {
    id: string;
    code: string;
    name: string;
    status: string;
    start_date: string;
    end_date: string | null;
    billing_periodicity: string | null;
    flat_amount: string | null;
    conditions: string | null;
    notes: string | null;
    customer_name: string;
    customer_tax_id: string | null;
  };
  signature: Firma | null;
}

const PERIODICIDAD: Record<string, string> = {
  MONTHLY: 'Mensual',
  QUARTERLY: 'Trimestral',
  BIANNUAL: 'Semestral',
  ANNUAL: 'Anual',
};

export function ContractDetailScreen(): React.ReactNode {
  const { api } = useSession();
  const { id } = useParams<{ id: string }>();

  const [detalle, setDetalle] = useState<Detalle | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    let cancelado = false;
    api
      .get<Detalle>(`/contracts/${id}`)
      .then((datos) => {
        if (!cancelado) setDetalle(datos);
      })
      .catch((caught: unknown) => {
        if (!cancelado) {
          setError(
            caught instanceof ApiError ? caught.message : 'No se ha podido cargar el contrato.',
          );
        }
      })
      .finally(() => {
        if (!cancelado) setCargando(false);
      });
    return () => {
      cancelado = true;
    };
  }, [api, id]);

  const c = detalle?.contract;
  const firma = detalle?.signature ?? null;

  // La copia no se manda desde aquí: se deja en la bandeja de salida. Que el
  // servidor de correo esté caído no puede bloquear a quien está dando de alta.
  const [enviando, setEnviando] = useState(false);
  const [avisoEnvio, setAvisoEnvio] = useState<string | null>(null);

  const enviarCopia = useCallback(() => {
    setEnviando(true);
    setAvisoEnvio(null);
    api
      .post<{ to: string }>(`/contracts/${id}/enviar-copia`, {})
      .then((res) => setAvisoEnvio(`Copia en cola para ${res.to}`))
      .catch((caught: unknown) => {
        setAvisoEnvio(
          caught instanceof ApiError ? caught.message : 'No se ha podido encolar la copia.',
        );
      })
      .finally(() => setEnviando(false));
  }, [api, id]);

  const tabs: Tab[] = [
    {
      id: 'condiciones',
      label: 'Contrato',
      render: () => (
        <>
          <DatumGroup title="Datos">
            <Datum label="Código">{c?.code}</Datum>
            <Datum label="Cliente">{c?.customer_name}</Datum>
            <Datum label="NIF">{c?.customer_tax_id}</Datum>
            <Datum label="Inicio">{day(c?.start_date ?? null)}</Datum>
            <Datum label="Fin">{day(c?.end_date ?? null)}</Datum>
            <Datum label="Facturación">
              {PERIODICIDAD[c?.billing_periodicity ?? ''] ?? c?.billing_periodicity}
            </Datum>
            <Datum label="Importe">{money(c?.flat_amount ?? null)}</Datum>
          </DatumGroup>
          <DatumGroup title="Condiciones">
            <Datum label="Texto">{c?.conditions}</Datum>
            <Datum label="Notas">{c?.notes}</Datum>
          </DatumGroup>
        </>
      ),
    },
    {
      id: 'firma',
      label: 'Firma',
      render: () =>
        firma === null ? (
          <p className="screen__note">
            Este contrato no está firmado. Un contrato sin firma no obliga a nadie.
          </p>
        ) : (
          <>
            <DatumGroup title="Quién firmó">
              <Datum label="Nombre">{firma.signerName}</Datum>
              <Datum label="Cargo">{firma.signerPosition ?? '—'}</Datum>
              <Datum label="Fecha">{dateTime(firma.signedAt)}</Datum>
            </DatumGroup>

            <DatumGroup title="Trazo">
              {firma.strokeSvg !== null ? (
                <div
                  className="signature"
                  aria-label={`Firma de ${firma.signerName}`}
                  // SVG de caminos que compuso nuestra propia app, sin script.
                  dangerouslySetInnerHTML={{ __html: firma.strokeSvg }}
                />
              ) : (
                <p className="screen__error">
                  {firma.unavailableReason ?? 'El trazo no está disponible'}
                </p>
              )}
            </DatumGroup>

            <DatumGroup title="Cadena de custodia">
              <Datum label="Recogida por">{firma.capturedBy ?? '—'}</Datum>
              <Datum label="Terminal">{firma.deviceId ?? '—'}</Datum>
              <Datum label="Huella de lo firmado">
                <code>{firma.signedDocumentHash}</code>
              </Datum>
            </DatumGroup>
          </>
        ),
    },
  ];

  return (
    <DetailShell
      title={c?.name ?? 'Contrato'}
      subtitle={
        <Link to="/contratos">← Contratos</Link>
      }
      badges={c === undefined ? null : <span className="pill">{c.status}</span>}
      actions={
        c === undefined ? null : (
          <>
            {/* El contrato en sí se imprime siempre: firmado o para firmar. La
                huella del documento sellado sólo existe cuando se firmó en la
                tablet; sin ella se usa el identificador, que basta para que
                cada contrato tenga su trabajo de renderizado propio. */}
            <PdfButton
              kind="SERVICE_CONTRACT"
              entityId={c.id}
              sourceHash={firma?.signedDocumentHash ?? c.id}
              label="Imprimir contrato"
            />
            {firma !== null && (
          <>
            <PdfButton
              kind="CONTRACT"
              entityId={c.id}
              sourceHash={firma.signedDocumentHash}
              label="Imprimir copia"
            />
            <button
              type="button"
              className="button button--quiet button--small"
              onClick={enviarCopia}
              disabled={enviando}
            >
              {enviando ? 'Encolando…' : 'Enviar copia al cliente'}
            </button>
            {avisoEnvio && <span className="hint">{avisoEnvio}</span>}
          </>
            )}
          </>
        )
      }
      tabs={tabs}
      loading={cargando}
      error={error}
    />
  );
}
